/**
 * OpenAPI API Client (hand-authored wrapper over generated types)
 *
 * This module is HAND-AUTHORED: it wires openapi-fetch with the auth interceptor
 * and the network error-recovery layer (timeout/offline/retry), and the
 * classified error copy. Only the TYPE definitions it consumes
 * (`@/types/api.generated`) are auto-generated from the backend's OpenAPI spec.
 * To regenerate those types: ./scripts/sync-api-types.sh (from repo root; canonical
 * entrypoint — do NOT run `npm run generate:api` directly, it drops the
 * spec-hash header and pre-commit will reject the commit)
 *
 * Usage:
 *   import { api } from '@/api/generated-client'
 *
 *   // All paths are type-safe and autocompleted
 *   const { data, error } = await api.GET('/auth/me')
 *   const { data, error } = await api.POST('/auth/login', {
 *     body: { email: 'user@example.com', password: '***' }
 *   })
 */

import createClient from 'openapi-fetch'
import type { paths } from '@/types/api.generated'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import { classifyNetworkError, type NetworkErrorState } from '@/api/network-error'
import { useNetworkStatus } from '@/composables/useNetworkStatus'

// Base URL for API requests
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'
const REQUEST_TIMEOUT_MS = 15000
const UNAUTHORIZED_COPY = classifyNetworkError({
  response: { status: 401 } as Response,
})

// Guard: `useAuthStore()` / `useUIStore()` / `useNetworkStatus()` throw
// ("getActivePinia() was called with no active Pinia") when invoked outside a
// context with an active Pinia instance (e.g. certain background/microtask timing
// edge cases). A network-layer interceptor must never crash the whole request
// pipeline over this — fall through to `null` instead.
function safeStore<T>(getStore: () => T): T | null {
  try {
    return getStore()
  } catch {
    return null
  }
}

function safeReportNetworkError(): ((state: NetworkErrorState) => void) | null {
  try {
    return useNetworkStatus().reportError
  } catch {
    return null
  }
}

function fetchWithRecovery(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController()
  const timedOut = setTimeout(() => {
    controller.abort()
  }, REQUEST_TIMEOUT_MS)

  const safeReport = safeReportNetworkError()

  return globalThis
    .fetch(input, {
      ...init,
      signal: controller.signal,
    })
    .catch((thrown) => {
      const state = classifyNetworkError({
        thrown,
        timedOut: thrown instanceof DOMException && thrown.name === 'AbortError',
        offline: typeof navigator === 'undefined' ? false : !navigator.onLine,
      })

      if (state.retryable) {
        safeReport?.(state)
      }

      throw new Error(state.message)
    })
    .finally(() => {
      clearTimeout(timedOut)
    })
}

// Create the typed client
const client = createClient<paths>({
  baseUrl: API_BASE_URL,
  fetch: fetchWithRecovery,
})

// Add auth header middleware
client.use({
  async onRequest({ request }) {
    // Get token from auth store (Pinia)
    const authStore = safeStore(useAuthStore)
    if (authStore?.accessToken) {
      request.headers.set('Authorization', `Bearer ${authStore.accessToken}`)
    }
    return request
  },
  async onResponse({ request, response }) {
    const url = new URL(request.url)
    const path = url.pathname
    const isPublicEndpoint = path.includes('/public/') || path.includes('/line/')
    // Console logs in via /auth/console/login (NOT /auth/login). A 401 on the
    // login endpoint is a failed sign-in, not an expired session, so it MUST be
    // excluded from the forced-relogin path (which would clearAuthData + redirect
    // and mislabel it 登入已過期) — the login form surfaces its own inline error.
    // Mirrors U5/CRM, whose login endpoint IS /auth/login.
    const isLoginEndpoint = /\/auth\/(console\/)?login$/.test(path)
    const isLogoutEndpoint = path.endsWith('/auth/logout')

    let authHandled = false

    if (response.status === 401 && !isPublicEndpoint && !isLoginEndpoint && !isLogoutEndpoint) {
      const authStore = safeStore(useAuthStore)
      authStore?.clearAuthData()
      authHandled = true
      const uiStore = safeStore(useUIStore)
      uiStore?.notify({
        type: 'warning',
        title: UNAUTHORIZED_COPY.title,
        message: UNAUTHORIZED_COPY.message,
        duration: 0,
      })
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    // Handle errors - show notification
    if (!authHandled && !response.ok) {
      let state = classifyNetworkError({ response })

      try {
        const body = await response.clone().json()
        state = classifyNetworkError({
          response,
          serverMessage: typeof body?.message === 'string' ? body.message : undefined,
        })
      } catch {
        state = classifyNetworkError({ response })
      }

      if (state.retryable) {
        const safeReport = safeReportNetworkError()
        safeReport?.(state)
        return response
      }

      const uiStore = safeStore(useUIStore)
      uiStore?.notify({
        type: 'error',
        title: state.title,
        message: state.message,
      })
    }

    return response
  },
})

// Export the typed client
export const api = client

// Re-export types for convenience
export type { paths }
export type { components, operations } from '@/types/api.generated'
